import { Emit } from "@/api/apiHelper";
import NFT, { convertNFTCollectionToMap } from "@/models/nft/NFT";
import SolanaWallet from "@/models/nft/SolanaWallet";
import {
  showErrorSnackbar,
  showInformationSnackbar,
  showSuccessSnackbar,
} from "@/utils/snackbarUtils";
import camelcaseKeys from "camelcase-keys";
import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import isEqual from "lodash/isEqual";

/**
 * 👾 Module which handles details about the NFTs of the user
 */
@Module
export default class NFTModule extends VuexModule {
  // 📦 The user's Solana NFT wallet address
  public nftWallet: SolanaWallet | null = null;

  // 🚩 Flag for whether the user's wallet address will be visible
  public nftWalletAddressVisible: boolean = true;

  // 📦 The user's NFT collection
  public nftCollection: NFT[] = [];

  // 📦 The number of extra bot slots due to NFTs
  public extraNFTBotSlots: number | null = null;

  // 📦 The active NFT skins of the user
  public activeNFTSkins: any = {
    astral: null,
    clipper: null,
    endeavour: null,
    horizon: null,
    performer: null,
  };

  // 📦 The user's NFT collection divided by bot type
  private get nftCollectionByBotType(): any {
    return this.nftCollection.length > 0
      ? convertNFTCollectionToMap(this.nftCollection)
      : {};
  }

  /**
   * 👾 Mutation that sets the user's NFT wallet address
   * @param walletAddress
   */
  @Mutation
  public SET_NFT_WALLET_ADDRESS(wallet: SolanaWallet | null) {
    this.nftWallet = wallet;
  }

  /**
   * 👾 Mutation that sets whether the NFT wallet should be visible
   * @param nftWalletAddressVisible
   */
  @Mutation
  public SET_NFT_WALLET_ADDRESS_VISIBLE(nftWalletAddressVisible: boolean) {
    this.nftWalletAddressVisible = nftWalletAddressVisible;
  }

  /**
   * 👾 Mutation that user's NFT collection
   * @param nftCollection
   */
  @Mutation
  public SET_NFT_COLLECTION(nftCollection: NFT[]) {
    if (isEqual(this.nftCollection, nftCollection)) {
      return;
    }
    this.nftCollection = nftCollection;
  }

  /**
   * 👾 Mutation that sets the user's active NFT skins
   * @param newActiveNFTSkins
   */
  @Mutation
  public SET_ACTIVE_NFT_SKINS(newActiveNFTSkins: any) {
    if (isEqual(this.activeNFTSkins, newActiveNFTSkins)) {
      return;
    }
    this.activeNFTSkins = newActiveNFTSkins;
  }

  /**
   * 👾 Mutation that sets a specific NFT as skin to a bot type
   * @param details
   */
  @Mutation
  public SET_SPECIFIC_ACTIVE_NFT_SKIN(details: any) {
    this.activeNFTSkins[details.botType] = details.skin;
  }


  /**
   * 👾 Mutation that sets a specific NFT as skin to a bot type
   * @param details
   */
  @Mutation
  public SET_TOTAL_NFT_BOT_SLOTS(extraBotSlots: number) {
    this.extraNFTBotSlots = extraBotSlots;
  }

  /**
   * 🔨 Changes the user's wallet visibility
   * @param visible
   */
  @Action
  public async changeNFTWalletVisibility(visible: boolean) {
    this.context.commit("SET_NFT_WALLET_ADDRESS_VISIBLE", visible);
  }

  /**
   * 🚀 Fetches the data available for the current user's NFT collection and related Solana wallet
   */
  @Action
  public async fetchCollectionAndBoundedWallet() {
    try {
      let rawResponse = await Vue.prototype.$api.get(
        "/api/v1/nft/botstoken",
        null,
        Emit.None
      );
      let formattedResponse = camelcaseKeys(rawResponse.data, { deep: true });

      this.context.commit(
        "SET_NFT_WALLET_ADDRESS",
        new SolanaWallet(
          formattedResponse.walletType,
          formattedResponse.ownerAddress
        )
      );
      this.context.commit(
        "SET_NFT_COLLECTION",
        Object.keys(formattedResponse.nftTokenBots).length > 0
          ? formattedResponse.nftTokenBots
          : []
      );
      this.context.commit("SET_ACTIVE_NFT_SKINS", {
        ...this.activeNFTSkins,
        ...formattedResponse.nftSelectBotImages,
      });
      this.context.commit("SET_TOTAL_NFT_BOT_SLOTS", formattedResponse.totalBotsAllowed);
    } catch (error) {
      this.context.commit("SET_NFT_WALLET_ADDRESS", null);
      this.context.commit("SET_NFT_COLLECTION", []);
      this.context.commit("SET_TOTAL_NFT_BOT_SLOTS", null);
      this.context.commit("SET_ACTIVE_NFT_SKINS", {
        astral: null,
        clipper: null,
        endeavour: null,
        horizon: null,
        performer: null,
      });
    }
  }

  /**
   * 🚀 Fetches the data available for the current user's NFT collection and related Solana wallet
   */
  @Action
  public async refreshNFTCollectionAndWallet() {
    await Vue.prototype.$api.post("/api/v1/nft/restart", null, Emit.None);
  }

  /**
   * 🚀 Updates the selected bot type skin on the API
   */
  @Action
  public activateOrDeactivateBotSkin(details: any) {
    let previousStateOfSkin = this.activeNFTSkins[details.botType];

    let newSkin =
      this.activeNFTSkins[details.botType] != details.nft.numberInCollection
        ? details.nft.numberInCollection
        : null;

    this.context.commit("SET_SPECIFIC_ACTIVE_NFT_SKIN", {
      botType: details.botType,
      skin: newSkin,
    });

    Vue.prototype.$api
      .put(
        "/api/v1/nft/botstoken",
        {
          strategy_name: details.botType,
          selected_image_name: newSkin ? newSkin : "",
        },
        Emit.None
      )
      .then((response: any) =>
        newSkin
          ? showSuccessSnackbar(
              "Your NFT skin was successfully applied to all of your " +
                details.botType.charAt(0).toUpperCase() +
                details.botType.slice(1) +
                " bots!"
            )
          : showInformationSnackbar("Your NFT skin was successfully removed!")
      )
      .catch((error: any) => {
        if (!error.error.includes("Image is not available")) {
          showErrorSnackbar(error.error);
        }
        this.context.commit("SET_SPECIFIC_ACTIVE_NFT_SKIN", {
          botType: details.botType,
          skin: previousStateOfSkin,
        });
      });
  }

  /**
   * 🚀 Removes the bounded Solana wallet address on the API
   */
  @Action
  public removeBoundedSolanaAddress() {
    Vue.prototype.$api
      .delete("/api/v1/nft/botstoken", null, Emit.None)
      .then((response: any) => {
        this.context.commit("SET_NFT_WALLET_ADDRESS", null);
        this.context.commit("SET_NFT_COLLECTION", []);
        this.context.commit("SET_ACTIVE_NFT_SKINS", {});
      });
  }
}
